import { createContext } from "react";

/** Branding resolved from the page hostname (a `<slug>.reveshare.com` portal). */
export interface HostPortalBranding {
  /** The portal brand's BrandDetail id — signup sends it as homeBrandDetailId. */
  brandDetailId: string;
  /**
   * The brand's storefront username. On a portal host the subdomain already
   * identifies the brand, so `/join` resolves it from here rather than from a
   * URL parameter.
   */
  brandUsername: string | null;
  /**
   * The brand's own name. There is no separately configured portal name — a
   * brand already has one name, and a second was a distinction without a
   * difference.
   */
  portalName: string;
  portalLogo: string | null;
  /**
   * Accent colours for the portal's theme (FULL tier only). Null means "keep
   * the default scale" — a BASIC portal is name and logo only.
   */
  primaryColour: string | null;
  secondaryColour: string | null;
}

export interface HostPortalContextType {
  /** True while the hostname lookup is still in flight. */
  isLoading: boolean;
  /** Null on the marketplace apex, localhost, or any non-portal host. */
  branding: HostPortalBranding | null;
  /**
   * True when the hostname is portal-shaped but no brand holds it — a typo, a
   * revoked brand, or a stale link.
   *
   * This must render an "unknown brand" page. Falling through to the
   * marketplace would serve the whole Reveshare app on an address a visitor
   * believes belongs to one brand.
   */
  isUnknownPortal: boolean;
  /** The unclaimed slug, for the 404 copy. */
  unknownSlug: string | null;
}

/**
 * Host-derived branding, mounted once at the app root. The default value
 * (settled, unbranded) also serves any tree rendered outside the provider.
 */
export const HostPortalContext = createContext<HostPortalContextType>({
  isLoading: false,
  branding: null,
  isUnknownPortal: false,
  unknownSlug: null,
});
